import React, {useEffect, useMemo, useRef, useState} from 'react'
import {useMutation} from 'react-query'
import {aiRetryDelay, extractAxiosErrorMessage, postAiChat, shouldRetryAi} from '../core/_requests'
import type {ChatMessage} from '../core/_types'

const PANEL_BG: React.CSSProperties = {
  background: 'var(--card-bg)',
  border: '1px solid var(--card-border)',
  borderRadius: 14,
}

const TEXTAREA_STYLE: React.CSSProperties = {
  width: '100%',
  resize: 'none',
  background: 'transparent',
  border: '1px solid var(--card-border)',
  borderRadius: 10,
  color: 'var(--text-primary, inherit)',
  padding: '10px 14px',
  fontSize: 14,
  fontFamily: 'inherit',
  lineHeight: 1.4,
  outline: 'none',
}

const QUICK_PROMPTS = [
  'How are my active setups looking?',
  "How's my R performance this month?",
  "What's BTC doing right now?",
  "What's my best coin lately?",
] as const

function newId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})
}

type Props = {
  /** Compact mode for the floating drawer; full-page page uses default. */
  compact?: boolean
  /** Optional fixed height; defaults to a sensible dashboard height. */
  height?: number | string
  /**
   * Optional prompt to auto-submit when the panel mounts. Combined with
   * `initialPromptKey` so the parent can re-trigger submission even when
   * the same string is supplied twice in a row (e.g. user opens the
   * drawer, closes it, opens again with the same seed).
   */
  initialPrompt?: string | null
  /** Bumped value the panel watches to know when to (re-)submit. */
  initialPromptKey?: number
  /** Called once the panel has consumed the initial prompt. */
  onInitialPromptConsumed?: () => void
}

export const ChatPanel: React.FC<Props> = ({
  compact = false,
  height = 560,
  initialPrompt = null,
  initialPromptKey = 0,
  onInitialPromptConsumed,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  // Kept around so the inline Retry button can re-fire the request that just
  // failed without making the user retype anything.
  const [lastFailedPrompt, setLastFailedPrompt] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const lastConsumedKey = useRef<number>(-1)

  const isFirstMessage = messages.length === 0

  const mutation = useMutation({
    mutationFn: postAiChat,
    // Auto-retry transient failures (network drops, 5xx, 429). Auth errors
    // and explicit timeouts are surfaced immediately. See shouldRetryAi.
    retry: shouldRetryAi,
    retryDelay: aiRetryDelay,
    onSuccess: (data) => {
      if (data.session_id) setSessionId(data.session_id)
      setMessages((prev) => [
        ...prev,
        {
          id: newId(),
          role: 'assistant',
          content: data.message,
          createdAt: Date.now(),
          modelName: data.model_name ?? null,
          totalToken: data.total_token ?? null,
        },
      ])
      setErrorMessage(null)
      setLastFailedPrompt(null)
    },
    onError: (err, variables) => {
      if (process.env.NODE_ENV !== 'production') {
        // Local diagnostic — helps tell apart a true network failure from a
        // CORS-stripped 5xx (which is what we believe is happening in prod
        // when the upstream LLM returns 502 without proper CORS headers).
        const ax = err as any
        // eslint-disable-next-line no-console
        console.error('[AI Coach] request failed', {
          message: ax?.message,
          code: ax?.code,
          status: ax?.response?.status,
          responseData: ax?.response?.data,
          requestUrl: ax?.config?.url,
        })
      }
      setErrorMessage(
        extractAxiosErrorMessage(err, 'AI assistant is unavailable right now — please try again.')
      )
      setLastFailedPrompt(variables.prompt)
    },
  })

  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [messages.length, mutation.isLoading])

  const send = (
    text: string,
    opts?: {sessionOverride?: string | null; firstOverride?: boolean; skipEcho?: boolean}
  ) => {
    const trimmed = text.trim()
    if (!trimmed || mutation.isLoading) return
    // `skipEcho` lets Retry re-fire without appending the user bubble twice.
    if (!opts?.skipEcho) {
      setMessages((prev) => [
        ...prev,
        {id: newId(), role: 'user', content: trimmed, createdAt: Date.now()},
      ])
    }
    setInput('')
    setErrorMessage(null)
    setLastFailedPrompt(null)
    // `focus_coin` intentionally omitted — the backend / llm-chat
    // service detects the focus coin from free-form text using a
    // whitelist of known crypto symbols, which is far more accurate
    // than anything we could do client-side.
    mutation.mutate({
      prompt: trimmed,
      session_id: opts?.sessionOverride !== undefined ? opts.sessionOverride : sessionId,
      is_new_session: opts?.firstOverride !== undefined ? opts.firstOverride : isFirstMessage,
    })
  }

  const retryLastFailed = () => {
    if (!lastFailedPrompt || mutation.isLoading) return
    // The user bubble is already in the message list from the original attempt,
    // so we skip echoing it again.
    send(lastFailedPrompt, {skipEcho: true})
  }

  // Auto-submit `initialPrompt` whenever a fresh `initialPromptKey` arrives.
  useEffect(() => {
    if (!initialPrompt) return
    if (initialPromptKey === lastConsumedKey.current) return
    lastConsumedKey.current = initialPromptKey
    send(initialPrompt)
    onInitialPromptConsumed?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrompt, initialPromptKey])

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send(input)
    }
  }

  const resetSession = () => {
    setMessages([])
    setSessionId(null)
    setErrorMessage(null)
    setInput('')
  }

  const containerHeight = useMemo(
    () => (typeof height === 'number' ? `${height}px` : height),
    [height]
  )

  return (
    <div style={{...PANEL_BG, display: 'flex', flexDirection: 'column', height: containerHeight}}>
      {/* Quick prompts */}
      <div
        style={{
          padding: compact ? '10px 12px' : '14px 18px',
          borderBottom: '1px solid var(--card-border)',
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--text-muted)',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        >
          Quick prompts
        </span>
        {QUICK_PROMPTS.map((q) => (
          <button
            key={q}
            type='button'
            onClick={() => send(q)}
            disabled={mutation.isLoading}
            className='btn btn-sm'
            style={{
              fontSize: 12,
              padding: '4px 10px',
              borderRadius: 999,
              border: '1px solid var(--card-border)',
              background: 'transparent',
              color: 'var(--text-primary, inherit)',
            }}
          >
            {q}
          </button>
        ))}
        <div style={{marginLeft: 'auto', display: 'flex', gap: 8}}>
          {sessionId && (
            <button
              type='button'
              onClick={resetSession}
              className='btn btn-sm btn-light'
              style={{fontSize: 12, padding: '4px 10px'}}
              title='Start a new chat'
            >
              New chat
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: compact ? '12px' : '18px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {isFirstMessage && !mutation.isLoading && (
          <div style={{color: 'var(--text-muted)', fontSize: 13, lineHeight: 1.5}}>
            Hi — I'm your AI Coach. I can comment on your active setups, your recent R performance
            and live market data (CoinGlass, Arkham, OKX live price) for any coin you ask about.
            Pick a quick prompt above or type your own question.
          </div>
        )}
        {messages.map((m) => (
          <MessageBubble key={m.id} msg={m} />
        ))}
        {mutation.isLoading && (
          <div style={{color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic'}}>
            Thinking…
          </div>
        )}
        {errorMessage && (
          <div
            style={{
              color: '#dc3545',
              background: 'rgba(220, 53, 69, 0.08)',
              borderRadius: 8,
              padding: '8px 12px',
              fontSize: 13,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            <span style={{flex: 1, minWidth: 0}}>{errorMessage}</span>
            {lastFailedPrompt && (
              <button
                type='button'
                onClick={retryLastFailed}
                disabled={mutation.isLoading}
                className='btn btn-sm'
                style={{
                  fontSize: 12,
                  padding: '3px 10px',
                  borderRadius: 999,
                  border: '1px solid #dc3545',
                  background: 'transparent',
                  color: '#dc3545',
                  fontWeight: 600,
                }}
              >
                Retry
              </button>
            )}
          </div>
        )}
      </div>

      {/* Composer */}
      <div
        style={{
          borderTop: '1px solid var(--card-border)',
          padding: compact ? '10px 12px' : '14px 18px',
          display: 'flex',
          gap: 10,
          alignItems: 'flex-end',
        }}
      >
        <textarea
          rows={compact ? 2 : 3}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder='Ask anything… (Enter to send, Shift+Enter for newline)'
          style={TEXTAREA_STYLE}
          disabled={mutation.isLoading}
        />
        <button
          type='button'
          onClick={() => send(input)}
          disabled={mutation.isLoading || input.trim().length === 0}
          className='btn btn-primary'
          style={{minWidth: 96, height: 40}}
        >
          Send
        </button>
      </div>
    </div>
  )
}

const MessageBubble: React.FC<{msg: ChatMessage}> = ({msg}) => {
  const isUser = msg.role === 'user'
  return (
    <div style={{display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start'}}>
      <div
        style={{
          maxWidth: '78%',
          background: isUser ? 'rgba(232, 64, 64, 0.10)' : 'var(--card-border)',
          color: 'var(--text-primary, inherit)',
          borderRadius: 12,
          padding: '10px 14px',
          fontSize: 14,
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {msg.content}
        <div
          style={{
            marginTop: 6,
            fontSize: 10,
            color: 'var(--text-muted)',
            textAlign: isUser ? 'right' : 'left',
          }}
        >
          {formatTime(msg.createdAt)}
          {!isUser && msg.modelName && <> · {msg.modelName}</>}
        </div>
      </div>
    </div>
  )
}

export default ChatPanel
